import { useState } from 'react'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import DataTable from '../../components/DataTable'
import { formatDate } from '../../utils/formatters'
import { mockSubscriptions, mockRevenueByTier } from '../../data/mockData'
import StatsCard from '../../components/StatsCard'
import { DollarSign, TrendingUp, Users } from 'lucide-react'

const COLORS = ['#0d6e6e', '#f5a623', '#64748b']

/**
 * Subscriptions Analytics Page
 * Display subscription metrics and analytics
 */
export default function SubscriptionsPage() {
  const [sortKey, setSortKey] = useState('startDate')
  const [sortOrder, setSortOrder] = useState('desc')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  // Calculate metrics
  const activeCount = mockSubscriptions.filter((s) => s.status === 'Active').length
  const totalRevenue = mockSubscriptions.reduce((sum, s) => sum + (s.amount || 0), 0) * 12
  const churnRate = 2.3
  const mrr = mockSubscriptions.reduce((sum, s) => sum + (s.status === 'Active' ? s.amount : 0), 0)

  const planStats = [
    {
      name: 'Free',
      count: mockSubscriptions.filter((s) => s.tier === 'Free').length,
      percentage: 0,
    },
    {
      name: 'Plus',
      count: mockSubscriptions.filter((s) => s.tier === 'Plus').length,
      percentage: 15,
    },
    {
      name: 'Family',
      count: mockSubscriptions.filter((s) => s.tier === 'Family').length,
      percentage: 25,
    },
  ]

  const columns = [
    {
      key: 'userName',
      label: 'User',
      sortable: true,
    },
    {
      key: 'tier',
      label: 'Plan',
      render: (value) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value === 'Plus'
              ? 'bg-pawssible-teal-100 text-pawssible-teal-700'
              : value === 'Family'
                ? 'bg-pawssible-amber-100 text-pawssible-amber-700'
                : 'bg-pawssible-slate-100 text-pawssible-slate-700'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Monthly (AED)',
      render: (value) => `${value}`,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      render: formatDate,
    },
    {
      key: 'nextBillingDate',
      label: 'Next Billing',
      render: formatDate,
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Subscriptions</h1>
        <p className="text-pawssible-slate-600 mt-1">Subscription analytics and revenue metrics</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          label="Monthly Revenue (AED)"
          value={mrr.toLocaleString()}
          icon={DollarSign}
          color="teal"
        />
        <StatsCard
          label="Active Subscriptions"
          value={activeCount}
          icon={Users}
          color="amber"
        />
        <StatsCard
          label="Annual Revenue (AED)"
          value={`${(totalRevenue / 1000).toFixed(1)}k`}
          icon={TrendingUp}
          color="teal"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Subscribers by Plan</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={planStats}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, count }) => `${name}: ${count}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {planStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => value} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue by Tier */}
        <div className="card">
          <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Revenue by Tier</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockRevenueByTier}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip contentStyle={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }} />
              <Bar dataKey="value" fill="#0d6e6e" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <p className="text-sm font-medium text-pawssible-slate-600">Churn Rate</p>
          <p className="text-3xl font-bold text-pawssible-slate-900 mt-2">{churnRate}%</p>
          <p className="text-xs text-red-600 mt-2">Monthly churn</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-pawssible-slate-600">Lifetime Value</p>
          <p className="text-3xl font-bold text-pawssible-slate-900 mt-2">AED 450</p>
          <p className="text-xs text-pawssible-slate-600 mt-2">Average per subscriber</p>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">Active Subscriptions</h2>
        <DataTable
          columns={columns}
          data={mockSubscriptions}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
        />
      </div>
    </div>
  )
}
